export type SessionStatus = 'active' | 'completed';

export type TrackType =
  | 'periodic'
  | 'clock_in'
  | 'clock_out'
  | 'visit_start'
  | 'visit_end'
  | 'pause'
  | 'resume';

export interface WorkSession {
  id: number;
  marketerId: number;
  marketerName: string | null;
  workDate: string;
  clockInAt: Date;
  clockOutAt: Date | null;
  clockInLat: number;
  clockInLng: number;
  clockOutLat: number | null;
  clockOutLng: number | null;
  status: SessionStatus;
  totalDistanceMeters: number | null;
  totalStoresVisited: number;
  /** Total work seconds — derived server-side when session is completed. */
  durationSeconds: number | null;
  /** True when the persisted simplified_path was snapped to roads via Mapbox. */
  roadSnapped: boolean;
  /**
   * NULL = PAUSED (resumable today). Non-null = ENDED. Resolved into the
   * lifecycleState helper for UI consumers.
   */
  finalizedAt: Date | null;
}

/**
 * UI-friendly lifecycle state — derived from `status` + `finalizedAt`.
 *
 *  - 'active' → tracking right now
 *  - 'paused' → completed, can resume today
 *  - 'ended'  → completed and locked
 */
export type SessionLifecycleState = 'active' | 'paused' | 'ended';

export function deriveSessionLifecycleState(session: WorkSession): SessionLifecycleState {
  if (session.status === 'active') return 'active';
  return session.finalizedAt !== null ? 'ended' : 'paused';
}

export interface SessionTrackPoint {
  lat: number;
  lng: number;
  recordedAt: Date;
  trackType: TrackType;
  accuracy: number | null;
  visitId: number | null;
  storeId: number | null;
  storeName: string | null;
}

export interface PathSegment {
  /** Ordered lat/lng points forming a single continuous polyline. */
  points: Array<{lat: number; lng: number}>;
}

export interface SessionTrack {
  session: WorkSession;
  /**
   * One polyline per work period. Split-shift sessions yield multiple
   * segments; the dashboard renders them as separate Mapbox layers so
   * pause→resume gaps don't get drawn through with a fake straight line.
   */
  segments: PathSegment[];
  /**
   * Event markers (clock_in / clock_out / pause / resume / visit_*) in
   * chronological order — rendered as map pins.
   */
  markers: SessionTrackPoint[];
  /** true when rendered from the pre-computed simplified path. */
  simplified: boolean;
  /** true when the simplified path was snapped to roads. */
  roadSnapped: boolean;
}

export interface SessionFilters {
  marketerId?: number;
  from?: string; // YYYY-MM-DD
  to?: string;
  status?: SessionStatus;
  page?: number;
  limit?: number;
}
